import { useCallback, useEffect, useMemo, useState } from "react";
import type { DrawerSlotApi } from "../drawer/drawerSlot";
import type { WaitlistEntry } from "../../../../types/waitlist";
import type { WalkInFormTarget } from "./WalkInForm";

/** The drawer slot's nested screen name for New / Edit walk-in. */
export const WALK_IN_FORM_NESTED = "walkInForm";

/**
 * Desktop walk-ins drawer state, held in the drawer slot as "walkins"
 * (opening it closes the booking / appointment / time block drawer, and
 * those evict it). New / Edit walk-in is the slot's nested "walkInForm"; the
 * FAB opens it directly (`instant`: no list first, no slide-in).
 */
export function useWaitlistDrawer(
  drawerSlot: Pick<
    DrawerSlotApi,
    "slot" | "open" | "close" | "openNested" | "closeNested" | "setEvictor"
  >,
) {
  const [opened, setOpened] = useState(false);
  const [target, setTarget] = useState<WalkInFormTarget | null>(null);
  const [instant, setInstant] = useState(false);
  const {
    slot,
    open: slotOpen,
    close: slotClose,
    openNested,
    closeNested,
    setEvictor,
  } = drawerSlot;

  useEffect(() => {
    setEvictor("walkins", () => {
      setOpened(false);
      setTarget(null);
    });
  }, [setEvictor]);

  const open = useCallback(() => {
    slotOpen("walkins");
    setOpened(true);
  }, [slotOpen]);

  const close = useCallback(() => {
    slotClose("walkins");
    setOpened(false);
    setTarget(null);
  }, [slotClose]);

  const showForm = useCallback(
    (next: WalkInFormTarget, fromOutside: boolean) => {
      slotOpen("walkins");
      openNested(WALK_IN_FORM_NESTED);
      setOpened(true);
      setTarget(next);
      setInstant(fromOutside);
    },
    [slotOpen, openNested],
  );

  /** FAB "New walk-in": the drawer opens on the form (no list flash). */
  const openNewForm = useCallback(
    () => showForm({ mode: "new" }, true),
    [showForm],
  );
  /** Footer "New Walk-In": slides the form over the list. */
  const startNew = useCallback(
    () => showForm({ mode: "new" }, false),
    [showForm],
  );
  const startEdit = useCallback(
    (entry: WaitlistEntry) => showForm({ mode: "edit", entry }, false),
    [showForm],
  );
  /** Back / Cancel / saved: return to the list. */
  const closeForm = useCallback(() => {
    closeNested();
    setTarget(null);
  }, [closeNested]);

  const formOpen =
    opened &&
    !!target &&
    slot?.kind === "walkins" &&
    slot.nested === WALK_IN_FORM_NESTED;

  return useMemo(
    () => ({
      opened,
      open,
      close,
      form: formOpen ? target : null,
      instant,
      openNewForm,
      startNew,
      startEdit,
      closeForm,
    }),
    [
      opened,
      open,
      close,
      formOpen,
      target,
      instant,
      openNewForm,
      startNew,
      startEdit,
      closeForm,
    ],
  );
}

export type WaitlistDrawerState = ReturnType<typeof useWaitlistDrawer>;
